import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, ChevronLeft, Clock, Pencil } from 'lucide-react';
import { resolveLook } from '../lib/bookingSlots.js';
import BookingGrid from '../components/booking/BookingGrid.jsx';
import BookingConfirmation from '../components/booking/BookingConfirmation.jsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const input = 'w-full rounded-lg border border-slate-300 bg-white text-slate-900 px-3 py-2.5 text-sm placeholder:text-slate-400 focus:outline-hidden focus:border-slate-500 focus:ring-4 ring-slate-300/40';

/** Zone name + short abbreviation, e.g. "America/Denver (MST)". */
function zoneLabel(timeZone) {
  if (!timeZone) return 'Your local time zone';
  const abbr = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
  return `${timeZone.replace(/_/g, ' ')}${abbr ? ` (${abbr})` : ''}`;
}

/** Public self-scheduling page (/book/:slug), laid out from the agent's Booking Page settings. */
export default function PublicBooking() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // Default to the AGENT's timezone so days match the availability they saved; the
  // pencil switches to the visitor's own zone.
  const [useLocalTz, setUseLocalTz] = useState(false);

  useEffect(() => {
    let alive = true;
    // Always fetch fresh: availability changes the moment the agent saves.
    fetch(`${API_BASE}/booking/page/${encodeURIComponent(slug)}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        if (!data.ok) setError(data.error || 'Page not found');
        else { setPage(data.page); setSlots(data.slots || []); }
      })
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  async function submit(e) {
    e.preventDefault();
    if (!selected) return;
    setSubmitError(null);
    if (!form.phone.trim()) { setSubmitError('Phone is required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/booking/page/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, startAt: selected }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setConfirmed({ at: selected, ...form });
      const dest = data.redirectUrl || page?.redirectUrl;
      if (dest) setTimeout(() => { window.location.href = dest; }, 4000);
    } catch (err) {
      setSubmitError(err.message || 'Failed to book');
    } finally {
      setSubmitting(false);
    }
  }

  const look = resolveLook(page?.appearance);
  const timeZone = useLocalTz ? undefined : page?.timezone;
  const panel = 'rounded-xl border p-5 mb-4';
  const panelStyle = { borderColor: `${look.fontColor}22`, backgroundColor: 'rgba(255,255,255,0.6)' };

  return (
    <div className="min-h-screen px-4 py-8" style={{ backgroundColor: look.backgroundColor, color: look.fontColor }}>
      <div className="max-w-5xl mx-auto">
        <header className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {page?.logoUrl ? (
              <img src={page.logoUrl} alt={page.brand || 'Logo'} className="w-12 h-12 object-contain rounded-md" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: look.slotColor, color: look.slotFontColor }}>
                <Calendar className="w-6 h-6" />
              </div>
            )}
          </div>
          <h1 className="text-2xl font-medium">{page?.title || 'Please select a time slot that would best fit your schedule.'}</h1>
          <p className="text-sm mt-2 opacity-80">{page?.instructions || 'Appts typically last between 5-10 minutes'}</p>
        </header>

        {loading && <div className={panel} style={panelStyle}>Loading availability…</div>}
        {error && <div className={`${panel} text-rose-600`} style={panelStyle}>{error}</div>}

        {confirmed && (
          <div className="max-w-xl mx-auto text-slate-900 bg-white rounded-xl border border-slate-200 p-5">
            <BookingConfirmation page={page} confirmed={confirmed} brand={look.slotColor} timeZone={timeZone} />
          </div>
        )}

        {!confirmed && !loading && !error && !selected && (
          <>
            <div className="flex items-center justify-end gap-1.5 mb-3 text-[11px] font-semibold uppercase tracking-wide" data-testid="tz-row">
              <span data-testid="tz-label">Time zone - {zoneLabel(timeZone)}</span>
              {page?.timezone && (
                <button type="button" onClick={() => setUseLocalTz((v) => !v)} aria-label={useLocalTz ? 'Show agent time zone' : 'Show my time zone'}
                  title={useLocalTz ? `Show in ${page.timezone.replace(/_/g, ' ')}` : 'Show in my time zone'} className="p-1 rounded hover:bg-black/5">
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
            <BookingGrid
              slots={slots}
              timeZone={timeZone}
              appearance={look}
              stepMinutes={page?.durationMinutes || 30}
              daysOut={page?.maxBookingDaysOut || 7}
              selected={selected}
              onSelect={setSelected}
            />
          </>
        )}

        {!confirmed && !loading && !error && selected && (
          <div className="max-w-xl mx-auto text-slate-900 bg-white rounded-xl border border-slate-200 p-5">
            <button type="button" onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
              <ChevronLeft className="w-4 h-4" /> Pick a different time
            </button>
            <div className="flex items-center gap-2 text-lg font-semibold mb-4">
              <Clock className="w-5 h-5" style={{ color: look.slotColor }} />
              {new Date(selected).toLocaleString('en-US', { timeZone, weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </div>
            <form onSubmit={submit} className="grid gap-3">
              <Field label="Your name"><input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" /></Field>
              <Field label="Phone (required)"><input className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" required /></Field>
              <Field label="Email"><input className={input} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" /></Field>
              <Field label="Anything we should know?"><textarea className={`${input} resize-y`} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              {Array.isArray(page?.customFields) && page.customFields.map((cf) => {
                if (!cf.label) return null;
                const fieldKey = cf.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
                if (['name', 'phone', 'email', 'notes'].includes(fieldKey)) return null;
                return (
                  <Field key={cf.id || cf.label} label={`${cf.label}${cf.required ? ' (required)' : ''}`}>
                    <input className={input} type="text" required={cf.required} value={form[fieldKey] || ''} onChange={(e) => setForm({ ...form, [fieldKey]: e.target.value })} placeholder={`Enter ${cf.label}`} />
                  </Field>
                );
              })}
              {submitError && <div className="rounded-lg border border-rose-300 bg-rose-50 text-rose-600 text-sm px-3 py-2">{submitError}</div>}
              <button type="submit" disabled={submitting}
                className="mt-2 w-full rounded-lg py-3 font-semibold shadow-md hover:-translate-y-0.5 transition disabled:opacity-60"
                style={{ backgroundColor: look.slotColor, color: look.slotFontColor, border: `1px solid ${look.slotBorderColor}` }}>
                {submitting ? 'Booking…' : 'Confirm booking'}
              </button>
            </form>
          </div>
        )}

        <div className="text-center mt-8 text-xs opacity-50">Powered by CIVIX</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="grid gap-1">
      <span className="text-sm text-slate-600">{label}</span>
      {children}
    </label>
  );
}
